import { DialogComponent, DialogProps } from "_shared/_views/dialogs/dialog";
import Guid from "_shared/guids/guid";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";

type DialogContextType = {
  numDialogs: number;
  openDialog: (dialogProps: DialogProps) => Guid;
  popDialog: () => void;
  closeDialog: () => void;
  updateDialogProps: (id: Guid, dialogProps: DialogProps) => void;
};

type DialogInfo = {
  id: Guid;
  props: DialogProps;
  open: boolean;
};

export const DialogContext = createContext<DialogContextType>(undefined!);

export default function DialogProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [dialogs, setDialogs] = useState<DialogInfo[]>([]);
  const numDialogs = dialogs.length;

  const closeDialog = useCallback(() => setDialogs([]), []);

  const popDialog = useCallback(() => {
    setDialogs((prev) => {
      if (prev.length === 0) return prev;

      // Remove top dialog
      const [, ...rest] = prev;

      if (rest.length > 0) {
        // Re-open next dialog in stack
        rest[0] = { ...rest[0], open: true };
      }

      return rest;
    });
  }, []);

  const openDialog = useCallback((newDialogProps: DialogProps) => {
    const id = Guid.generate();

    setDialogs((prev) => {
      const prevLen = prev.length;

      const dialogPropsToStore: DialogProps = {
        ...newDialogProps,
        onClose: () => {
          newDialogProps.onClose?.();
          closeDialog();
        },
        onBack: popDialog,
        hideBackButton: prevLen < 1 || newDialogProps.resetStack,
        hideCloseButton: prevLen > 0 && !newDialogProps.resetStack,
      };

      const newDialog: DialogInfo = {
        id,
        open: true,
        props: dialogPropsToStore
      };

      if (newDialogProps.resetStack) return [newDialog];

      const updated = [...prev];
      if (updated.length > 0) updated[0] = { ...updated[0], open: false };
      updated.unshift(newDialog);
      return updated;
    });

    return id;
  },
    [closeDialog, popDialog],
  );

  const updateDialogProps = useCallback((id: Guid, newDialogProps: DialogProps) => {
    setDialogs((prev) => {
      const dialogIndex = prev.findIndex((d) => d.id.isEqualTo(id));
      if (dialogIndex === -1) return prev;

      const updated = [...prev];

      updated[dialogIndex] = {
        ...updated[dialogIndex],
        props: {
          ...newDialogProps,
          onClose: () => {
            newDialogProps.onClose?.();
            closeDialog();
          },
          onBack: popDialog,
          hideBackButton: prev.length <= 1,
          hideCloseButton: dialogIndex !== 0,
        },
      };

      return updated;
    });
  },
    [closeDialog, popDialog],
  );

  const dialogState = useMemo(
    () => ({
      numDialogs,
      openDialog,
      popDialog,
      closeDialog,
      updateDialogProps,
    }),
    [numDialogs, openDialog, popDialog, closeDialog, updateDialogProps],
  );

  return (
    <DialogContext.Provider value={dialogState}>
      {children}
      {dialogs.map((dialogInfo) => (
        <DialogComponent
          key={dialogInfo.id.value}
          open={dialogInfo.open}
          {...dialogInfo.props}
        />
      ))}
    </DialogContext.Provider>
  );
}

export function useDialog() {
  const context = useContext(DialogContext);
  if (context === undefined) throw new Error("useDialog must be used within a DialogProvider");
  return context;
}
